/**
 * Standard dropdown button component with navigation capabilities.
 * Features:
 * - Title with arrow indicator
 * - Click-outside detection for auto-closing
 * - Router-linked menu items
 */

import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  ReactNode,
  MouseEvent,
} from 'react'
import {
  dropdownContainer,
  dropdownMenu,
  dropdownItem,
} from './styles/DropdownButton.styles'
import {
  Button,
  StyledArrowDownLineIcon,
  StyledArrowUpLineIcon,
} from './styles/DropdownButton.styles'
import { Link, useLocation } from 'react-router-dom'

interface MenuItemProps {
  children?: ReactNode
  setIsOpen?: (isOpen: boolean) => void
  inactive?: boolean
  to?: string
  className?: string
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  as?: any
  [key: string]: any
}

/**
 * MenuItem component that acts as a router link and auto-closes dropdown when clicked
 */
const MenuItem = forwardRef<HTMLAnchorElement, MenuItemProps>(
  ({ children, setIsOpen, inactive = false, ...props }, ref) => {
    const location = useLocation()
    const isActive = props.to === location.pathname

    return (
      <Link
        css={dropdownItem}
        ref={ref}
        to={props.to || '#'}
        {...props}
        className={`${inactive ? 'inactive' : ''} ${isActive ? 'active' : ''} ${props.className || ''}`}
        onClick={(e) => {
          if (inactive) {
            e.preventDefault()

            return
          }

          if (setIsOpen) setIsOpen(false)
          if (props.onClick) props.onClick(e)
        }}
      >
        {children}
      </Link>
    )
  },
)

MenuItem.displayName = 'MenuItem'

interface DropdownButtonProps {
  title: string
  children?: ReactNode
  inactive?: boolean
}

/**
 * Main dropdown component that manages its own open/close state
 * @param title - Text shown on the dropdown button
 * @param children - MenuItem components to be rendered in dropdown
 * @param inactive - When true, button appears disabled and cannot be clicked
 */
const DropdownButton: React.FC<DropdownButtonProps> = ({
  title,
  children,
  inactive = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  // Check if any child MenuItem's route matches current location
  const isAnyChildActive = React.Children.toArray(children).some(
    (child: any) => {
      return child.props?.to === location.pathname
    },
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent<Document> | Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = () => {
    if (!inactive) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <div css={dropdownContainer} ref={dropdownRef}>
      <Button
        onClick={handleToggle}
        className={`${isOpen ? 'open' : ''} ${inactive ? 'inactive' : ''} ${isAnyChildActive ? 'active' : ''}`}
      >
        {title}{' '}
        {isOpen ? <StyledArrowUpLineIcon /> : <StyledArrowDownLineIcon />}
      </Button>
      {!inactive && (
        <div css={dropdownMenu} className={isOpen ? 'open' : ''}>
          {React.Children.map(children, (child: any) => {
            if (!child) return null
            const isChildActive = child.props?.to === location.pathname

            return React.cloneElement(child, {
              setIsOpen: setIsOpen || (() => {}),
              className: `${child.props?.className || ''} ${isChildActive ? 'active' : ''}`,
            })
          })}
        </div>
      )}
    </div>
  )
}

interface MenuButtonProps {
  title: string
  inactive?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  to: string
}

const MenuButton: React.FC<MenuButtonProps> = ({
  title,
  inactive = false,
  onClick,
  to,
}) => {
  const location = useLocation()
  const isActive = location.pathname === to

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`${inactive ? 'inactive' : ''} button-link`}
      style={{ textDecoration: 'none' }}
    >
      <Button
        className={`${inactive ? 'inactive' : ''} ${isActive ? 'active' : ''}`}
      >
        {title}
      </Button>
    </Link>
  )
}

export default DropdownButton

export { DropdownButton, MenuItem, MenuButton }
